import { useState, useEffect } from 'react';
import { getReceivablesReport } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const Reports = () => {
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getReceivablesReport();
        // API returns {receivables: [...], total_outstanding: number}
        setReceivables(res.data.receivables || []);
      } catch (err) { toast.error('Failed to load reports'); } finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const totalOutstanding = receivables.reduce((s, r) => s + r.outstanding, 0);
  const totalOverdue = receivables.reduce((s, r) => s + (r.overdue_count > 0 ? r.outstanding : 0), 0);

  if (loading) {
    return <div className="space-y-6"><Skeleton className="h-8 w-32" /><Skeleton className="h-64 w-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground">Receivables summary</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Total Outstanding</CardTitle><CreditCard className="h-4 w-4 text-expense" /></CardHeader>
          <CardContent><div className="text-2xl font-bold font-mono text-expense">{formatCurrency(totalOutstanding)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Overdue Amount</CardTitle><CreditCard className="h-4 w-4 text-destructive" /></CardHeader>
          <CardContent><div className="text-2xl font-bold font-mono text-destructive">{formatCurrency(totalOverdue)}</div></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CreditCard className="h-5 w-5" />Receivables by Customer</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total Sales</TableHead>
                  <TableHead className="text-right">Total Paid</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead className="text-center">Overdue Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receivables.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No receivables</TableCell></TableRow>
                ) : receivables.map((r) => (
                  <TableRow key={r.customer_name}>
                    <TableCell className="font-medium">{r.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(r.amount || r.total_sales || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-income">{formatCurrency(r.paid_amount || r.total_paid || 0)}</TableCell>
                    <TableCell className="text-right font-mono text-expense">{formatCurrency(r.outstanding || 0)}</TableCell>
                    <TableCell className="text-center">{(r.overdue_count > 0 || r.due_days > 0) ? <Badge variant="destructive">{r.overdue_count || 1}</Badge> : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
